import { RTEInitValue, extractTextWithNewLinesFromHtml } from '@talentmesh/rte';
import { z } from 'zod';
import { AssessmentStatuses } from '@/Models/AssessmentData';
import UIStrings from '@/Utils/UIStrings';
import ValidationErrors from '@/Utils/ValidationErrors';

export const JobDescriptionCharactersLimit = 6000;

const autocompleteOptionSchema = z.object({
    id: z.string().optional(),
    label: z.string(),
    group: z.string().optional(),
});

export const jobAdSchema = z
    .object({
        title: z
            .string()
            .min(1, { message: ValidationErrors.Required })
            .max(256, {
                error: ({ maximum }) => ValidationErrors.JobAdTitleMaxSymbolsValidationError(maximum),
            }),
        hardSkillsSwitch: z.boolean(),
        hardSkills: z.array(autocompleteOptionSchema),
        softSkillsSwitch: z.boolean(),
        softSkills: z.array(autocompleteOptionSchema),
        description: z.string(),
        descriptionLen: z
            .number()
            .min(1, { message: ValidationErrors.JobDescriptionRequiredError })
            .max(JobDescriptionCharactersLimit, { message: ValidationErrors.PleaseLimitDescriptionError }),
        companyDescription: z.string(),
        status: z.nativeEnum(AssessmentStatuses),
    })
    .superRefine((values, ctx) => {
        if (values.hardSkillsSwitch && values.hardSkills.length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: ValidationErrors.PleaseAddAtLeastOneSkillRequiredError('hard'),
                path: ['hardSkills'],
            });
        }

        if (values.softSkillsSwitch && values.softSkills.length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: ValidationErrors.PleaseAddAtLeastOneSkillRequiredError('soft'),
                path: ['softSkills'],
            });
        }

        const plainText = extractTextWithNewLinesFromHtml(values.companyDescription || '');

        if (plainText.length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: UIStrings.Required,
                path: ['companyDescription'],
            });
        } else if (plainText.length >= 4000) {
            ctx.addIssue({
                code: 'custom',
                message: UIStrings.CompanyDescriptionInputLengthValidation,
                path: ['companyDescription'],
            });
        }
    });

export type JobAdFormValues = z.infer<typeof jobAdSchema>;

export const defaultJobAdFormValues: JobAdFormValues = {
    title: '',
    hardSkills: [],
    hardSkillsSwitch: true,
    softSkills: [],
    softSkillsSwitch: true,
    description: RTEInitValue, // we use this initial value for correct RTE behavior is we add only list in description
    descriptionLen: 0,
    companyDescription: RTEInitValue,
    status: AssessmentStatuses.Active,
};
